// ---------------------------------------------------------------------------
// Funnel Builder Constants
// ---------------------------------------------------------------------------
// Dimension configurations and design tokens.
// Only dimensions supported by the backend FunnelService are included.
// ---------------------------------------------------------------------------

import type { DimensionType } from './types';
import type { FilterOptions } from '@/services/v11/filterOptionsService';
import type { DataSourceId } from '@/components/v11/discovery';

export interface DimensionConfig {
  readonly key: string;
  readonly label: string;
  readonly type: DimensionType;
  readonly description: string;
  readonly applicableTo?: readonly DataSourceId[]; // Which datasources support this dimension
  readonly comingSoon?: boolean;
}

// Audience Dimensions (filters profiles/people)
export const AUDIENCE_DIMENSIONS: readonly DimensionConfig[] = [
  {
    key: 'platform',
    label: 'Platform',
    type: 'audience',
    description: 'Instagram, TikTok, X, YouTube',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'gender',
    label: 'Gender',
    type: 'audience',
    description: 'Male, Female',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'age',
    label: 'Generation',
    type: 'audience',
    description: 'Gen Z, Millennials, Gen X, Boomers',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'country',
    label: 'Country',
    type: 'audience',
    description: 'United States, Brazil, United Kingdom',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'followerTier',
    label: 'Follower Tier',
    type: 'audience',
    description: 'Nano, Micro, Macro, Mega',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'ethnicity',
    label: 'Ethnicity',
    type: 'audience',
    description: 'White, Black, Hispanic, Asian',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'industry',
    label: 'Industry',
    type: 'audience',
    description: 'Technology, Finance, Healthcare',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'lifestyle',
    label: 'Lifestyle',
    type: 'audience',
    description: 'Luxury, Wellness, Adventure',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'statusClass',
    label: 'Status Class',
    type: 'audience',
    description: 'Elite, Upper, Middle, Working Class',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'sentiment',
    label: 'Sentiment',
    type: 'audience',
    description: 'Positive, Neutral, Negative',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'brand',
    label: 'Brand',
    type: 'audience',
    description: 'Nike, Adidas, Jordan, Apple',
    applicableTo: ['profiles', 'content', 'comments', 'teams'],
  },
  {
    key: 'item',
    label: 'Item',
    type: 'audience',
    description: 'Hat, Sneakers, T-shirt, Jacket',
    applicableTo: ['profiles', 'content', 'comments', 'teams'],
  },
  {
    key: 'team',
    label: 'Teams & Players',
    type: 'audience',
    description: 'Brooklyn Nets, SL Benfica, Cristiano Ronaldo',
    applicableTo: ['profiles', 'content', 'comments', 'brands'],
  },
  {
    key: 'brandCategory',
    label: 'Brand Category',
    type: 'audience',
    description: 'Apparel, Beverages, Technology',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'author',
    label: 'Author',
    type: 'audience',
    description: 'Filter by specific author/profile',
    applicableTo: ['content', 'comments'],
  },
  {
    key: 'language',
    label: 'Language',
    type: 'audience',
    description: 'English, Spanish, Portuguese',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'toxic',
    label: 'Toxic',
    type: 'audience',
    description: 'Profiles with toxic comments',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'spam',
    label: 'Spam',
    type: 'audience',
    description: 'Profiles with spam comments',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
] as const;

// Content Dimensions (filters posts/content that profiles engage with)
// Backend implements these as cross-view dimensions (discovery_content_*_profiles_view)
// Type 'content' is for visual distinction only - backend treats as audience cross-view
// All content dimensions apply to 'profiles' datasource (filter profiles by content they interact with)
export const CONTENT_DIMENSIONS: readonly DimensionConfig[] = [
  {
    key: 'channel',
    label: 'Channel',
    type: 'content',
    description: 'NBA, Lakers, ESPN, Bleacher Report',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'hashtag',
    label: 'Hashtag',
    type: 'content',
    description: '#nike, #nba, #sports',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'emotion',
    label: 'Emotion',
    type: 'content',
    description: 'Joy, Excitement, Sadness',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'mediaType',
    label: 'Media Type',
    type: 'content',
    description: 'Video, Image, Carousel',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
  },
  {
    key: 'category',
    label: 'Category',
    type: 'content',
    description: 'Sports, Entertainment, Lifestyle',
    applicableTo: ['profiles', 'content', 'comments', 'teams'],
  },
  {
    key: 'post',
    label: 'Post',
    type: 'content',
    description: 'Specific post by caption',
    applicableTo: ['profiles', 'content', 'comments'],
  },
  // Discovery-specific dimensions
  {
    key: 'paidOrganic',
    label: 'Paid/Organic',
    type: 'content',
    description: 'Paid promotion vs organic content',
    applicableTo: ['profiles', 'content', 'comments', 'brands', 'teams'],
    comingSoon: true,
  },
] as const;

// Maps dimension key → FilterOptions property for API-sourced values
// Dimensions NOT in this map use local constants (MEDIA_TYPE_OPTIONS, etc.)
export const DIMENSION_FILTER_OPTIONS_MAP: Partial<Record<string, keyof FilterOptions>> = {
  platform: 'platforms',
  gender: 'genders',
  country: 'countries',
  followerTier: 'followerTiers',
  emotion: 'emotions',
  ethnicity: 'ethnicities',
  industry: 'industries',
  lifestyle: 'lifestyles',
  statusClass: 'statusClasses',
  language: 'languages',
  brandCategory: 'brandCategories',
} as const;

// Generation definitions mapping to age_interval IDs
// Each generation selects multiple age interval IDs when applied as a filter
export const GENERATION_OPTIONS = [
  { key: 'gen_alpha', label: 'Gen Alpha (0-12)', ids: [1, 2, 3] },
  { key: 'gen_z', label: 'Gen Z (13-24)', ids: [4, 5] },
  { key: 'millennials', label: 'Millennials (25-40)', ids: [6, 7, 8] },
  { key: 'gen_x', label: 'Gen X (41-60)', ids: [9, 10, 11] },
  { key: 'boomers', label: 'Boomers+ (61+)', ids: [12, 13] },
] as const;

// Layer width progression formula
export const calculateLayerWidth = (stepIndex: number): number => {
  return Math.max(100 - stepIndex * 8, 50); // Min 50%
};
